import React, { useState } from 'react';
import {
  FamilyFormData,
  DependentRow,
  HousingAcquisitionType,
  HousingLoanCategory,
  FurusatoFilingMethod,
} from '../domain/tax';
import './FamilyForm.css';

// 後方互換のため UI 入力型を再エクスポートする。
export type { FamilyFormData };

interface FamilyFormProps {
  familyData: FamilyFormData;
  onFamilyDataChange: (data: FamilyFormData) => void;
}

interface SectionHeadingProps {
  /** 折りたたみ対象の内容要素の id（aria-controls）。 */
  contentId: string;
  title: string;
  expanded: boolean;
  onToggle: () => void;
}

/** セクション見出しのトグル。開閉状態を aria-expanded で示す。 */
const SectionHeading: React.FC<SectionHeadingProps> = ({ contentId, title, expanded, onToggle }) => (
  <h3 className="form-section-heading">
    <button
      type="button"
      className="section-toggle"
      aria-expanded={expanded}
      aria-controls={contentId}
      onClick={onToggle}
    >
      <span className="section-toggle-icon" aria-hidden="true">
        {expanded ? '▾' : '▸'}
      </span>
      <span className="section-toggle-label">{title}</span>
    </button>
  </h3>
);

/** 新規の扶養親族行。 */
function createDependentRow(): DependentRow {
  return { age: 0, incomeMan: 0, livingTogether: false };
}

const FamilyForm: React.FC<FamilyFormProps> = ({ familyData, onFamilyDataChange }) => {
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const handleInputChange = <K extends keyof FamilyFormData>(field: K, value: FamilyFormData[K]) => {
    onFamilyDataChange({ ...familyData, [field]: value });
  };

  const updateDependent = (index: number, patch: Partial<DependentRow>) => {
    const next = familyData.dependents.map((row, i) => {
      if (i !== index) {
        return row;
      }
      const merged: DependentRow = { ...row, ...patch };
      // 同居老親等は70歳以上の行のみ有効。
      if (merged.age < 70) {
        merged.livingTogether = false;
      }
      return merged;
    });
    onFamilyDataChange({ ...familyData, dependents: next });
  };

  const addDependent = () => {
    onFamilyDataChange({ ...familyData, dependents: [...familyData.dependents, createDependentRow()] });
  };

  const removeDependent = (index: number) => {
    onFamilyDataChange({
      ...familyData,
      dependents: familyData.dependents.filter((_, i) => i !== index),
    });
  };

  const moveInYearMonth =
    familyData.housingMoveInYear > 0
      ? `${familyData.housingMoveInYear}-${String(
          familyData.housingMoveInMonth > 0 ? familyData.housingMoveInMonth : 1,
        ).padStart(2, '0')}`
      : '';

  const handleMoveInChange = (value: string) => {
    if (!value) {
      onFamilyDataChange({ ...familyData, housingMoveInYear: 0, housingMoveInMonth: 0 });
      return;
    }
    const [year, month] = value.split('-');
    onFamilyDataChange({
      ...familyData,
      housingMoveInYear: Number(year) || 0,
      housingMoveInMonth: Number(month) || 0,
    });
  };

  return (
    <div className="family-form">
      <div className="form-section">
        <SectionHeading
          contentId="settings-section-content"
          title="扶養家族と控除の設定"
          expanded={settingsExpanded}
          onToggle={() => setSettingsExpanded((value) => !value)}
        />
        <div id="settings-section-content" className="form-content" hidden={!settingsExpanded}>
          <h4 className="form-subheading">扶養家族</h4>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={familyData.spouse}
                onChange={(e) => handleInputChange('spouse', e.target.checked)}
              />
              配偶者あり
            </label>
          </div>

          {familyData.spouse && (
            <div className="dependents-row">
              <div className="dependent-field">
                <label htmlFor="spouseAnnualIncome">配偶者年収（万円）</label>
                <input
                  type="number"
                  id="spouseAnnualIncome"
                  value={familyData.spouseAnnualIncome}
                  onChange={(e) => handleInputChange('spouseAnnualIncome', Number(e.target.value))}
                  min="0"
                  step="1"
                />
              </div>

              <div className="dependent-field">
                <label htmlFor="spouseAge">配偶者の年齢（歳）</label>
                <input
                  type="number"
                  id="spouseAge"
                  value={familyData.spouseAge}
                  onChange={(e) => handleInputChange('spouseAge', Number(e.target.value))}
                  min="0"
                  step="1"
                />
              </div>
            </div>
          )}

          <div className="dependents-table-container">
            <table className="dependents-table">
              <colgroup>
                <col className="col-age" />
                <col className="col-income" />
                <col className="col-living" />
                <col className="col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">年齢</th>
                  <th scope="col">合計所得（万円）</th>
                  <th scope="col">同居老親等</th>
                  <th scope="col" aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {familyData.dependents.map((row, index) => (
                  <tr key={`dependent-${index}`}>
                    <td>
                      <input
                        type="number"
                        aria-label={`扶養親族${index + 1}の年齢`}
                        value={row.age}
                        min="0"
                        max="120"
                        step="1"
                        onChange={(e) => updateDependent(index, { age: Number(e.target.value) })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        aria-label={`扶養親族${index + 1}の合計所得`}
                        value={row.incomeMan}
                        min="0"
                        step="0.0001"
                        onChange={(e) => updateDependent(index, { incomeMan: Number(e.target.value) })}
                      />
                    </td>
                    <td className="living-together-cell">
                      <input
                        type="checkbox"
                        aria-label={`扶養親族${index + 1}は同居老親等`}
                        checked={row.age >= 70 && row.livingTogether}
                        disabled={row.age < 70}
                        onChange={(e) => updateDependent(index, { livingTogether: e.target.checked })}
                      />
                    </td>
                    <td className="action-cell">
                      <button
                        type="button"
                        className="dependents-remove"
                        onClick={() => removeDependent(index)}
                        aria-label={`扶養親族${index + 1}を削除`}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="dependents-add" onClick={addDependent}>
              扶養親族を追加
            </button>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={familyData.workingStudent}
                onChange={(e) => handleInputChange('workingStudent', e.target.checked)}
              />
              本人は勤労学生
            </label>
          </div>

          <h4 className="form-subheading">生命保険料控除</h4>

          <div className="dependents-row">
            <div className="dependent-field">
              <label htmlFor="lifeInsuranceGeneral">一般生命保険料（年間払込額・円）</label>
              <input
                type="number"
                id="lifeInsuranceGeneral"
                value={familyData.lifeInsuranceGeneral}
                onChange={(e) => handleInputChange('lifeInsuranceGeneral', Number(e.target.value))}
                min="0"
                step="1"
              />
            </div>
            <div className="dependent-field">
              <label htmlFor="lifeInsuranceMedical">介護医療保険料（年間払込額・円）</label>
              <input
                type="number"
                id="lifeInsuranceMedical"
                value={familyData.lifeInsuranceMedical}
                onChange={(e) => handleInputChange('lifeInsuranceMedical', Number(e.target.value))}
                min="0"
                step="1"
              />
            </div>
            <div className="dependent-field">
              <label htmlFor="lifeInsurancePension">個人年金保険料（年間払込額・円）</label>
              <input
                type="number"
                id="lifeInsurancePension"
                value={familyData.lifeInsurancePension}
                onChange={(e) => handleInputChange('lifeInsurancePension', Number(e.target.value))}
                min="0"
                step="1"
              />
            </div>
          </div>

          <h4 className="form-subheading">住宅ローン控除</h4>

          <div className="dependents-row">
            <div className="dependent-field">
              <label htmlFor="housingMoveIn">居住開始年月</label>
              <input
                type="month"
                id="housingMoveIn"
                value={moveInYearMonth}
                onChange={(e) => handleMoveInChange(e.target.value)}
              />
            </div>

            <div className="dependent-field">
              <label htmlFor="housingAcquisition">取得区分</label>
              <select
                id="housingAcquisition"
                value={familyData.housingAcquisition}
                onChange={(e) =>
                  handleInputChange('housingAcquisition', e.target.value as HousingAcquisitionType)
                }
              >
                <option value="NEW">新築等</option>
                <option value="USED">既存（中古）</option>
              </select>
            </div>

            <div className="dependent-field">
              <label htmlFor="housingCategory">住宅区分</label>
              <select
                id="housingCategory"
                value={familyData.housingCategory}
                onChange={(e) =>
                  handleInputChange('housingCategory', e.target.value as HousingLoanCategory)
                }
              >
                <option value="LONG_TERM">認定長期優良・低炭素</option>
                <option value="ZEH">ZEH水準省エネ</option>
                <option value="ENERGY_SAVING">省エネ基準適合</option>
                <option value="OTHER">その他</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={familyData.housingChildRearing}
                onChange={(e) => handleInputChange('housingChildRearing', e.target.checked)}
              />
              子育て世帯・若者夫婦世帯
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={familyData.housingSpecialAcquisition}
                onChange={(e) => handleInputChange('housingSpecialAcquisition', e.target.checked)}
              />
              特定取得・特別特定取得
            </label>
          </div>

          <div className="dependents-row">
            <div className="dependent-field">
              <label htmlFor="housingYearEndBalance">住宅ローン年末残高（円）</label>
              <input
                type="number"
                id="housingYearEndBalance"
                value={familyData.housingYearEndBalance}
                onChange={(e) => handleInputChange('housingYearEndBalance', Number(e.target.value))}
                min="0"
                step="1"
              />
            </div>

            <div className="dependent-field">
              <label htmlFor="housingCreditPossibleAmount">住宅借入金等特別控除可能額（円）</label>
              <input
                type="number"
                id="housingCreditPossibleAmount"
                aria-describedby="housingCreditPossibleAmount-note"
                value={familyData.housingCreditPossibleAmount}
                onChange={(e) => handleInputChange('housingCreditPossibleAmount', Number(e.target.value))}
                min="0"
                step="1"
              />
              <small id="housingCreditPossibleAmount-note" className="field-note">
                <span className="field-note-line">0 のときは年末残高（借入限度額まで）から自動計算します。</span>
                <span className="field-note-line">
                  源泉徴収票の「住宅借入金等特別控除可能額」は申告する年分の額です（前年度の額ではありません）。
                </span>
                <span className="field-note-line">
                  源泉徴収票に記載がない場合（控除額が所得税で控除しきれた場合など）は 0 のままで構いません。
                </span>
                <span className="field-note-line">
                  経過措置など本アプリが近似している区分では、源泉徴収票の実額を入力するほうが正確です。
                </span>
              </small>
            </div>
          </div>

          <h4 className="form-subheading">ふるさと納税</h4>

          <div className="dependents-row">
            <div className="dependent-field">
              <label htmlFor="furusatoFilingMethod">ふるさと納税の申告方法</label>
              <select
                id="furusatoFilingMethod"
                value={familyData.furusatoFilingMethod}
                onChange={(e) =>
                  handleInputChange('furusatoFilingMethod', e.target.value as FurusatoFilingMethod)
                }
              >
                <option value="FINAL_RETURN">確定申告</option>
                <option value="ONE_STOP">ワンストップ特例</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyForm;
